import { useTranslation } from 'react-i18next'
import { Outlet, useMatches } from 'react-router'

import { BrandLockup, Toaster } from '@/shared/ui'

import { BreadcrumbProvider, Breadcrumbs } from './Breadcrumbs'
import { Header } from './Header'
import { ThemeToggle } from './ThemeToggle'
import { VerificationBanner } from './VerificationBanner'

/**
 * What a route can ask of the shell, through its `handle`.
 *
 * `fullBleed` drops `<main>`'s own padding (`px-gutter pt-6 pb-4`) so the page
 * can paint edge-to-edge bands — the homepage only, today. ⚠️ A page that sets
 * it owns its own gutters: every band must put `px-gutter` on its content (as
 * the header does) or text runs to the edge of a phone and stops lining up
 * with the header's lockup.
 *
 * ⚠️ **There is no max-width on the shell** (Task 6, owner, 1 October 2026:
 * the brand reference fills the screen with a 5% gutter; ours was a centred
 * 83rem column). A screen whose content would stretch to unreadable line
 * lengths at 2560px caps that content itself — never the shell.
 */
export type ShellHandle = { fullBleed?: boolean }

function isShellHandle(handle: unknown): handle is ShellHandle {
  return typeof handle === 'object' && handle !== null && 'fullBleed' in handle
}

/** The chrome every portal renders inside — §3.4. */
export function AppShell() {
  const { t } = useTranslation('common')
  const fullBleed = useMatches().some(
    (match) => isShellHandle(match.handle) && match.handle.fullBleed === true,
  )

  return (
    // Mounted once, here: a toast raised on any screen in any portal lands in
    // the same viewport, and nothing below has to carry a provider.
    <Toaster label={t('toast.region')} closeLabel={t('toast.close')}>
      <BreadcrumbProvider>
        {/* `overflow-x-clip` is the backstop for the rule that the document must
            never scroll sideways. A single mis-sized child otherwise shifts the
            whole page — and in Arabic the overflow lands on the edge the reader
            starts from, so it is not even visible as a scrollbar. */}
        <div className="flex min-h-dvh flex-col overflow-x-clip">
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-md focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-fg-onbrand"
          >
            {t('nav.skipToContent')}
          </a>

          <Header />
          <VerificationBanner />
          <Breadcrumbs />

          <main
            id="main"
            className={
              fullBleed ? 'w-full min-w-0 flex-1' : 'w-full min-w-0 flex-1 px-gutter pt-6 pb-4'
            }
          >
            <Outlet />
          </main>

          {/* The reference's footer: lockup at the start, one line saying
              what the product is for in the middle, the copyright at the end
              (Task 4, 1 October 2026: a three-column grid so the line is truly
              centred whatever the two ends measure; the reference's own
              `space-between` only centres it by accident of equal widths). On a
              phone the line drops under the other two at full width, as the
              reference's `footer p { order: 3 }` does.
              ⚠️ The line says **Egypt**, not "MENA": the product operates in
              Egypt only (owner ruling), and a footer is not the place to claim
              a region it does not serve. The lockup is decorative; the
              copyright beside it already names the brand to a screen reader. */}
          {/* ⚠️ The gutter (`px-gutter`) is on the *inner* grid, beside
              `<main>`'s and the header's, so the lockup starts on the same
              line as the content above it at every width. */}
          <footer className="border-t border-line bg-sunken py-10 text-[13px] text-fg-muted">
            <div className="grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-[15px] px-gutter md:grid-cols-[1fr_auto_1fr]">
              <BrandLockup
                wordmark={t('brand.wordmark')}
                size="lg"
                className="justify-self-start"
              />
              <p className="col-span-2 row-start-2 m-0 md:col-span-1 md:col-start-2 md:row-start-1 md:text-center">
                {t('footer.tagline')}
              </p>
              {/* The dark-mode switch (Task 5, 1 October 2026) shares the end
                  cell with the copyright from `md` up, so the three-part line
                  keeps its centre; the copyright stays last, as in the
                  reference. ⚠️ **On a phone the wrapper dissolves**
                  (`max-md:contents`): squeezed beside the lockup, at 320px both
                  the switch and the copyright wrapped onto two lines each
                  (measured). So the copyright keeps its old place opposite the
                  lockup and the switch takes a row of its own under the line,
                  its text on the gutter (`-ms-2` cancels the button's padding). */}
              <div className="flex items-center justify-end gap-x-3 justify-self-end max-md:contents">
                <ThemeToggle className="justify-self-start max-md:col-start-1 max-md:row-start-3 max-md:-ms-2 md:-me-2" />
                <small className="justify-self-end text-xs max-md:col-start-2 max-md:row-start-1">
                  {t('footer.copyright', { year: new Date().getFullYear() })}
                </small>
              </div>
            </div>
          </footer>
        </div>
      </BreadcrumbProvider>
    </Toaster>
  )
}
